// <s-card-gallery> keeps a product card's photo track, dots and cursor in sync:
// a fine pointer's horizontal position picks the photo, dots jump to one, and swipes update the dots.
class SCardGallery extends HTMLElement {
  #ac;
  #track;
  #dots = [];
  #index = 0;

  connectedCallback() {
    this.#track = this.querySelector('[data-gallery-track]');
    this.#dots = [...this.querySelectorAll('[data-gallery-dot]')];
    if (!this.#track || this.#dots.length < 2) return;
    this.#ac = new AbortController();
    const signal = this.#ac.signal;
    const count = this.#dots.length;

    this.#dots.forEach((dot, i) => {
      dot.addEventListener('click', () => this.#show(i), { signal });
    });

    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
    this.#track.addEventListener('pointermove', (event) => {
      if (!finePointer.matches || event.pointerType !== 'mouse') return;
      const rect = this.#track.getBoundingClientRect();
      const ratio = (event.clientX - rect.left) / rect.width;
      // The track is laid out in reading order, so mirror the zones in RTL.
      const forward = getComputedStyle(this.#track).direction === 'rtl' ? 1 - ratio : ratio;
      this.#show(Math.min(count - 1, Math.max(0, Math.floor(forward * count))));
    }, { signal });
    this.#track.addEventListener('pointerleave', (event) => {
      if (event.pointerType === 'mouse') this.#show(0);
    }, { signal });

    this.#track.addEventListener('scroll', () => {
      const width = this.#track.clientWidth;
      if (!width) return;
      this.#mark(Math.round(Math.abs(this.#track.scrollLeft) / width));
    }, { signal, passive: true });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #show(index) {
    if (index === this.#index) return;
    const slide = this.#track.children[index];
    if (!slide) return;
    this.#track.scrollTo({ left: slide.offsetLeft - this.#track.children[0].offsetLeft, behavior: 'instant' });
    this.#mark(index);
  }

  #mark(index) {
    this.#index = index;
    this.#dots.forEach((dot, i) => {
      if (i === index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }
}

if (!customElements.get('s-card-gallery')) customElements.define('s-card-gallery', SCardGallery);
